import { useMemo } from "react";
import "./TwelfthReport.css";

const QTag = ({ q, a, impacts }) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:'5px 10px',alignItems:'center',background:'#f0f4ff',border:'1px solid #c7d7fa',borderRadius:'6px',padding:'7px 12px',marginBottom:'12px',fontSize:'12px',lineHeight:1.5}}>
    <span style={{background:'#3b5bdb',color:'#fff',fontWeight:700,padding:'2px 8px',borderRadius:4,fontSize:'10px',whiteSpace:'nowrap'}}>📋 FORM QUESTION</span>
    <span style={{color:'#1e3a8a',fontStyle:'italic'}}>"{q}"</span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#047857',fontWeight:700}}>You answered: <strong>{a}</strong></span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#7c3aed',fontSize:'11px'}}>📊 Impacts: {impacts}</span>
  </div>
);

function getSectorType(sector) {
  const s = (sector || "").toLowerCase();
  if (["agri","farm","fisheri","dairy","horticulture","rural","nabard","rkvy"].some(k => s.includes(k))) return "primary";
  if (["manufactur","electronic","ev ","electric vehicle","biotech","construction","industrial","hardware","textile","machinery","semiconductor"].some(k => s.includes(k))) return "secondary";
  return "tertiary";
}

const SECTOR_MARKET = {
  tertiary: { tam:"₹12,00,000 Cr+", samBase:"₹1,50,000 – ₹2,50,000 Cr", cagr:"18–28% p.a.", drivers:"Digital India push, smartphone adoption, B2B SaaS wave, D2C explosion, AI integration" },
  secondary: { tam:"₹8,00,000 Cr+",  samBase:"₹80,000 – ₹1,40,000 Cr",   cagr:"12–18% p.a.", drivers:"PLI schemes, Make in India, EV transition, Industry 4.0, export demand growth" },
  primary:   { tam:"₹6,00,000 Cr+",  samBase:"₹40,000 – ₹80,000 Cr",     cagr:"10–15% p.a.", drivers:"RKVY & NABARD funding, FPO expansion, rural digitization, govt procurement" },
};

const BM_SOM  = { "B2B":"2–4%", "B2C":"5–10%", "B2B2C":"3–6%", "C2C":"4–8%", "SME":"2–5%" };
const BM_Y1   = {
  "B2B":   "₹25 L – ₹1 Cr  (10–40 enterprise clients at ₹5–₹25 L/yr)",
  "B2C":   "₹8 L – ₹60 L   (500–5,000 users at ₹100–₹1,500/mo)",
  "B2B2C": "₹15 L – ₹80 L  (hybrid: business contracts + consumer base)",
  "C2C":   "₹5 L – ₹30 L   (GMV-driven; 5–12% take-rate per transaction)",
  "SME":   "₹20 L – ₹75 L  (SME contracts at ₹10–₹30 L/yr per client)",
};
const REV_FIT = {
  subscription: { score:"⭐⭐⭐⭐⭐ Excellent", note:"Predictable MRR, highest investor preference, compounding retention value." },
  fees:         { score:"⭐⭐⭐ Good",          note:"Strong unit economics per transaction — scales naturally with volume growth." },
  ads:          { score:"⭐⭐ Moderate",        note:"Viable only at 10K+ DAU — investors prefer switching to subscription early." },
};
const COMP_WINDOW = {
  "prototype":       "🟢 Wide open — first-mover advantage available for 12–18 months",
  "validation":      "🟡 Early opportunity — competitors still proving concept; enter now",
  "early traction":  "🟠 Narrowing — move fast to establish brand before competition scales",
  "scaling":         "🔴 Competitive — must differentiate on niche, geography, or model",
  "mature/dominant": "🔴 Late entry — requires strong niche focus or regional differentiation",
};
const MATURITY_STATUS = {
  "idea stage":    { status:"Pre-seed ready",      runway:"6–12 months to build PoC" },
  "poc-mvp":       { status:"Seed/Incubator ready", runway:"12–18 months to prove PMF" },
  "pmf":           { status:"Seed/Series-A ready",  runway:"18–24 months to scale" },
  "scaling stage": { status:"Series-A/B ready",     runway:"Scale now — institutional capital priority" },
};

const TwelfthReport = ({ res }) => {
  const sector   = res?.sector ?? "your sector";
  const bm       = res?.businessModel ?? "";
  const revModel = (res?.revenueStreams ?? "").toLowerCase();
  const compSt   = (res?.competitorStage ?? "").toLowerCase();
  const maturity = (res?.productMaturity ?? "").toLowerCase();

  const sectorType = useMemo(() => getSectorType(sector), [sector]);
  const mkt    = SECTOR_MARKET[sectorType];
  const revFit = REV_FIT[revModel];
  const matInv = MATURITY_STATUS[maturity];

  const rows = [
    {
      sno:"11 A", dim:"TAM — Total Addressable Market",
      scale: mkt.tam,
      ctx: `Full India ${sector} market`,
      insight: `This is the maximum market if you captured 100% share. Use ₹${mkt.tam} as your pitch anchor — investors want TAM > ₹10,000 Cr to justify venture returns.`,
    },
    {
      sno:"11 B", dim:"SAM — Serviceable Addressable Market",
      scale: mkt.samBase,
      ctx: `Realistic segment for ${sector} with your ${bm || "chosen"} model`,
      insight: `Your SAM is shaped by your business model and target segment. Keep your SAM pitch tight — investors prefer a clear, defensible segment over a vague large one.`,
    },
    {
      sno:"11 C", dim:"SOM — Serviceable Obtainable Market",
      scale: `${BM_SOM[bm] ?? "3–6%"} of SAM (Year 1–3)`,
      ctx: `Reachable at your current stage and capital level`,
      insight: `A ${BM_SOM[bm] ?? "3–6%"} penetration of your SAM in 3 years is realistic at seed stage. Investors want SOM to be believable — not hockey-stick fantasy.`,
    },
    {
      sno:"11 D", dim:"Year 1 Revenue Target",
      scale: BM_Y1[bm] ?? "₹10 L – ₹50 L (benchmark)",
      ctx: `Based on ${bm || "your"} model and current stage`,
      insight: `Set this as your 12-month MRR/ARR milestone before your next funding round. Every rupee of Year 1 revenue reduces dilution in your Series A negotiation.`,
    },
    {
      sno:"11 E", dim:"Revenue Model Fit Score",
      scale: revFit?.score ?? "⭐⭐⭐ Good",
      ctx: `${res?.revenueStreams ?? "Your model"} in ${sector}`,
      insight: revFit?.note ?? `Optimise your revenue model for predictability and retention — the two metrics investors weight most heavily at early stage.`,
    },
    {
      sno:"11 F", dim:"Market Entry Window",
      scale: COMP_WINDOW[compSt] ?? "🟡 Moderate opportunity",
      ctx: `Competitor stage: ${res?.competitorStage ?? "—"}`,
      insight: `Entry timing relative to competitor maturity determines your GTM aggressiveness — earlier entry means more organic share capture before the market consolidates.`,
    },
    {
      sno:"11 G", dim:"Investor Stage Readiness",
      scale: matInv?.status ?? "Seed ready",
      ctx: `Current stage: ${res?.productMaturity ?? "—"}`,
      insight: `${matInv?.runway ?? "Validate and target seed round in 12–18 months"}. Match your funding ask to your current evidence level — stage mismatch is the #1 reason early-stage pitches fail.`,
    },
    {
      sno:"11 H", dim:"Sector Growth Tailwinds",
      scale: mkt.cagr,
      ctx: `India ${sectorType} sector CAGR`,
      insight: `The ${sector} space is growing at ${mkt.cagr}. Cite these tailwinds in your pitch deck: ${mkt.drivers}.`,
    },
  ];

  return (
    <div className="iv-rb-twelfth-container">
      <div className="iv-rb-twelfth-final-table">
        <span className="iv-rb-twelfth-corner iv-rb-twelfth-corner-top-left" />
        <span className="iv-rb-twelfth-corner iv-rb-twelfth-corner-top-right" />
        <span className="iv-rb-twelfth-corner iv-rb-twelfth-corner-bottom-left" />
        <span className="iv-rb-twelfth-corner iv-rb-twelfth-corner-bottom-right" />

        <div className="iv-rb-twelfth-module-title">
          <span className="iv-rb-module-prefix">Module 11:</span> Market Sizing &amp; Revenue Opportunity Analysis
          <span className="iv-rb-premium-tag">★ Report A+ Exclusive</span>
        </div>

        <QTag
          q="What sector do you operate in, what is your business model, revenue stream, and current product stage?"
          a={`Sector: ${sector} · Model: ${bm || "—"} · Revenue: ${res?.revenueStreams || "—"} · Stage: ${res?.productMaturity || "—"}`}
          impacts="TAM/SAM/SOM sizing, Year 1 revenue target, revenue model fit score, market entry window, investor stage readiness"
        />

        <div className="iv-rb-twelfth-sub-context">
          Sector: <strong>{sector}</strong> · Type: <strong>{sectorType}</strong> · Model: <strong>{bm || "—"}</strong> · Competitor stage: <strong>{res?.competitorStage || "—"}</strong>
        </div>

        <div className="iv-rb-twelfth-table-scroll-wrapper">
          <table className="iv-rb-twelfth-inner-table">
            <thead>
              <tr>
                <th className="iv-rb-twelfth-col-sno">S.NO</th>
                <th>Market Dimension</th>
                <th>India Market Scale</th>
                <th>Your Segment Context</th>
                <th>Strategic Insight</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="iv-rb-twelfth-col-sno">{r.sno}</td>
                  <td><strong>{r.dim}</strong></td>
                  <td className="iv-rb-twelfth-scale">{r.scale}</td>
                  <td className="iv-rb-twelfth-user-val"><em>{r.ctx}</em></td>
                  <td className="iv-rb-twelfth-highlight">{r.insight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="iv-rb-twelfth-footer">
          © MarsMate AI · Market data derived from your sector ({sector}), business model ({bm || "—"}), and product stage ({res?.productMaturity || "—"}). Use as directional guidance for investor conversations.
        </div>
      </div>
    </div>
  );
};

export default TwelfthReport;
